// src/constants/menus/menu-builder.ts
import { MenuConfig, UserRole, UserGroup, MenuItemType } from './types';
import { ROLE_TO_GROUPS, MENU_CONFIGS } from './menu-config';

export class PermissionChecker {
  static hasAccess(menuConfig: MenuConfig, userRoles: UserRole[], userGroups?: UserGroup[]): boolean {
    if (userRoles.includes('admin')) return true;

    if (menuConfig.roles && menuConfig.roles.length > 0) {
      const hasRole = menuConfig.roles.some(role => userRoles.includes(role));
      if (!hasRole) return false;
    }

    if (menuConfig.groups && menuConfig.groups.length > 0) {
      const effectiveGroups = userGroups || this.getUserGroups(userRoles);
      const hasGroup = menuConfig.groups.some(group => effectiveGroups.includes(group));
      if (!hasGroup) return false;
    }

    return true;
  }

  static getUserGroups(roles: UserRole[]): UserGroup[] {
    const groups = new Set<UserGroup>();
    roles.forEach(role => {
      const roleGroups = ROLE_TO_GROUPS[role] || [];
      roleGroups.forEach(group => groups.add(group));
    });
    return Array.from(groups);
  }

  static canAccessPayments(role: UserRole): boolean {
    return ['right_owner', 'licensee', 'admin', 'staff'].includes(role);
  }

  static canManagePayments(role: UserRole): boolean {
    return ['admin', 'staff'].includes(role);
  }

  static canPublishContent(role: UserRole): boolean {
    return ['right_owner', 'admin'].includes(role);
  }

  static canApproveCertificates(role: UserRole): boolean {
    return ['admin', 'staff', 'legal'].includes(role);
  }

  static canAccessMarketplace(role: UserRole): boolean {
    return true;
  }

  static canAccessCreatorTools(role: UserRole): boolean {
    return ['right_owner', 'admin'].includes(role);
  }
}

export class MenuBuilder {
  private userRoles: UserRole[];
  private userGroups: UserGroup[];

  constructor(roles: UserRole[]) {
    this.userRoles = roles;
    this.userGroups = PermissionChecker.getUserGroups(roles);
  }

  build(configs: MenuConfig[]): MenuItemType[] {
    return configs
      .filter(config => this.shouldInclude(config))
      .map(config => this.transformConfig(config))
      .filter(Boolean) as MenuItemType[];
  }

  buildAll(): MenuItemType[] {
    return this.build(MENU_CONFIGS);
  }

  private shouldInclude(config: MenuConfig): boolean {
    // if (config.disabled) return false;
    return PermissionChecker.hasAccess(config, this.userRoles, this.userGroups);
  }

  private transformConfig(config: MenuConfig): MenuItemType | null {
    const item: MenuItemType = {
      key: config.key,
      label: config.label,
      icon: config.icon,
      url: config.url,
      disabled: config.disabled,
    };

    if (config.badge) {
      if (typeof config.badge === 'string') {
        item.badge = { text: config.badge, variant: 'danger' };
      } else {
        item.badge = config.badge;
      }
    }

    if (config.children && config.children.length > 0) {
      const filteredChildren = config.children
        .filter(child => this.shouldInclude(child))
        .map(child => this.transformConfig(child))
        .filter(Boolean) as MenuItemType[];

      if (filteredChildren.length > 0) {
        item.children = filteredChildren;
      } else if (!config.url) {
        return null;
      }
    }

    return item;
  }
}

export const buildMenuForRoles = (roles: UserRole[]): MenuItemType[] => {
  const builder = new MenuBuilder(roles);
  return builder.buildAll();
};

export const buildMenuForRole = (role: UserRole): MenuItemType[] => {
  return buildMenuForRoles([role]);
};

export const buildMenuForGroups = (groups: UserGroup[]): MenuItemType[] => {
  const roles = Object.entries(ROLE_TO_GROUPS)
    .filter(([_, roleGroups]) => roleGroups.some(group => groups.includes(group)))
    .map(([role]) => role as UserRole);
  return buildMenuForRoles(roles);
};